import React, { useEffect, useMemo, useState } from 'react';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { formatMagentoDateTime } from '../utils/datetime';
import { buildOrderMetrics } from '../utils/orderMetrics';
import './SlaMonitorPage.css';

const pad = (value) => String(value).padStart(2, '0');

const formatMagentoRangeDate = (date, endOfDay = false) => {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = endOfDay ? '23' : pad(date.getHours());
  const minutes = endOfDay ? '59' : pad(date.getMinutes());
  const seconds = endOfDay ? '59' : pad(date.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[\",\n]/.test(str)) {
    return `"${str.replace(/\"/g, '""')}"`;
  }
  return str;
};

const downloadCsv = (filename, headers, rows) => {
  const lines = [
    headers.map(csvEscape).join(','),
    ...rows.map((row) => row.map(csvEscape).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const uniqByEntityId = (orders = []) => {
  const map = new Map();
  orders.forEach((order) => {
    const key = order?.entity_id || order?.increment_id;
    if (key) map.set(String(key), order);
  });
  return Array.from(map.values());
};

const SlaMonitorPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shippingFilter, setShippingFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const restBase = (localStorage.getItem('restBase') || '/rest/V1').trim() || '/rest/V1';
  const pickingStatus = (localStorage.getItem('pickingStatus') || 'picking').trim();

  const fetchBacklog = async () => {
    setLoading(true);
    setError('');
    try {
      const now = new Date();
      const last24Hours = new Date(now.getTime() - (24 * 60 * 60 * 1000));
      const completedFromDate = formatMagentoRangeDate(last24Hours);
      const pageSize = 100;
      const fetchAllOrderPages = async (baseUrl, label) => {
        const allItems = [];
        let currentPage = 1;
        let totalCount = null;

        while (totalCount === null || allItems.length < totalCount) {
          const pagedUrl =
            `${baseUrl}&searchCriteria[pageSize]=${pageSize}` +
            `&searchCriteria[currentPage]=${currentPage}`;
          const response = await apiFetch(pagedUrl, { headers: getAuthHeaders() });
          if (!response.ok) {
            throw new Error((await response.text()) || `Failed to load ${label} (${response.status})`);
          }

          const data = await response.json();
          const items = Array.isArray(data?.items) ? data.items : [];
          totalCount = Number(data?.total_count);
          if (!Number.isFinite(totalCount)) totalCount = allItems.length + items.length;
          allItems.push(...items);

          if (!items.length || items.length < pageSize) break;
          currentPage += 1;
        }

        return allItems;
      };

      const processingUrl =
        `${restBase}/orders?` +
        'searchCriteria[filter_groups][0][filters][0][field]=status' +
        '&searchCriteria[filter_groups][0][filters][0][value]=processing' +
        '&searchCriteria[filter_groups][0][filters][0][condition_type]=eq';
      const pickingUrl =
        `${restBase}/orders?` +
        'searchCriteria[filter_groups][0][filters][0][field]=status' +
        `&searchCriteria[filter_groups][0][filters][0][value]=${encodeURIComponent(pickingStatus)}` +
        '&searchCriteria[filter_groups][0][filters][0][condition_type]=eq';
      const completedUrl =
        `${restBase}/orders?` +
        'searchCriteria[filter_groups][0][filters][0][field]=status' +
        '&searchCriteria[filter_groups][0][filters][0][value]=complete' +
        '&searchCriteria[filter_groups][0][filters][0][condition_type]=eq' +
        '&searchCriteria[filter_groups][1][filters][0][field]=updated_at' +
        `&searchCriteria[filter_groups][1][filters][0][value]=${encodeURIComponent(completedFromDate)}` +
        '&searchCriteria[filter_groups][1][filters][0][condition_type]=gteq';

      const [processingItems, pickingItems, completedItems] = await Promise.all([
        fetchAllOrderPages(processingUrl, 'processing backlog'),
        fetchAllOrderPages(pickingUrl, 'picking backlog'),
        fetchAllOrderPages(completedUrl, 'completed orders'),
      ]);
      const items = uniqByEntityId([...processingItems, ...pickingItems, ...completedItems]);

      const detailed = await Promise.all(
        items.map(async (order) => {
          try {
            const detailRes = await apiFetch(`${restBase}/orders/${order.entity_id}`, {
              headers: getAuthHeaders(),
            });
            if (!detailRes.ok) throw new Error(await detailRes.text());
            return await detailRes.json();
          } catch (detailErr) {
            console.warn('Failed to fetch order detail', order.entity_id, detailErr);
            return order;
          }
        })
      );

      const filteredDetails = detailed.filter(Boolean).filter((order) => {
        const metrics = buildOrderMetrics(order, { now, pickingStatus });
        const status = String(order?.status || '').toLowerCase();
        if (status !== 'complete') return true;
        const completeAt = metrics.sla?.completeAt;
        return completeAt && completeAt.getTime() >= last24Hours.getTime();
      });

      setOrders(filteredDetails);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load backlog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBacklog();
  }, []);

  const normalized = useMemo(() => {
    const now = new Date();
    return orders.map((order) => {
      const metrics = buildOrderMetrics(order, { now, pickingStatus });
      const status = (order.status || '').toLowerCase();
      const isProcessing = status === 'processing';
      const isPicking = status === pickingStatus.toLowerCase();
      const isComplete = status === 'complete';
      const sla = metrics.sla;
      const sla48Overdue = sla.sla48Breached;
      const sla72Overdue = sla.sla72Breached;
      const isInPickingPhase = !!sla.pickingAt && !sla.completeAt;
      const isBeforePicking = !sla.pickingAt && !sla.completeAt;

      return {
        ...order,
        ...metrics,
        sla,
        statusLabel: isPicking ? 'Picking' : isProcessing ? 'Processing' : isComplete ? 'Complete' : order.status,
        sla48Overdue,
        sla72Overdue,
        isInPickingPhase,
        isBeforePicking,
        isCompletedLast24h: isComplete && !!sla.completeAt,
      };
    });
  }, [orders, pickingStatus]);

  const filtered = useMemo(() => {
    return normalized.filter((order) => {
      const shipping = order.shippingLabel.toLowerCase();
      if (shippingFilter === 'xpect' && !shipping.includes('xpect')) return false;
      if (shippingFilter === 'click' && !shipping.includes('click')) return false;
      if (shippingFilter === 'next' && !shipping.includes('next')) return false;
      if (statusFilter === 'before-picking' && !order.isBeforePicking) return false;
      if (statusFilter === 'in-picking' && !order.isInPickingPhase) return false;
      if (statusFilter === 'completed-24h' && !order.isCompletedLast24h) return false;
      return true;
    });
  }, [normalized, shippingFilter, statusFilter]);

  const totals = useMemo(() => {
    const base = {
      total: filtered.length,
      processing: 0,
      picking: 0,
      completed24h: 0,
      sla48Overdue: 0,
      sla72Overdue: 0,
    };
    for (const order of filtered) {
      if (order.statusLabel.toLowerCase() === 'processing') base.processing += 1;
      if (order.statusLabel.toLowerCase() === 'picking') base.picking += 1;
      if (order.isCompletedLast24h) base.completed24h += 1;
      if (order.sla48Overdue) base.sla48Overdue += 1;
      if (order.sla72Overdue) base.sla72Overdue += 1;
    }
    return base;
  }, [filtered]);

  const shippingBreakdown = useMemo(() => {
    const map = new Map();
    filtered.forEach((order) => {
      const key = order.shippingLabel || 'N/A';
      if (!map.has(key)) {
        map.set(key, { label: key, total: 0, sla48Overdue: 0, sla72Overdue: 0 });
      }
      const entry = map.get(key);
      entry.total += 1;
      if (order.sla48Overdue) entry.sla48Overdue += 1;
      if (order.sla72Overdue) entry.sla72Overdue += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [filtered]);

  const handleExportCsv = () => {
    const headers = [
      'order',
      'status',
      'shipping_method',
      'created_at',
      'picking_at',
      'complete_at',
      'age_hours',
      'sla48_overdue',
      'sla72_overdue',
    ];
    const rows = filtered.map((order) => {
      const pickingAt = order.sla?.pickingAt ? order.sla.pickingAt.toISOString() : '';
      const completeAt = order.sla?.completeAt ? order.sla.completeAt.toISOString() : '';
      const age = order.ageHours !== null && order.ageHours !== undefined ? order.ageHours.toFixed(1) : '';
      return [
        order.increment_id || order.entity_id || '',
        order.statusLabel || order.status || '',
        order.shippingLabel || '',
        order.created_at || '',
        pickingAt,
        completeAt,
        age,
        order.sla48Overdue ? 'yes' : 'no',
        order.sla72Overdue ? 'yes' : 'no',
      ];
    });
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`sla-monitor-${stamp}.csv`, headers, rows);
  };

  return (
    <div className="sla-page">
      <div className="sla-shell">
        <header className="sla-header">
          <div>
            <h1>SLA Monitor</h1>
            <p>Current backlog (Processing + Picking) with SLA 48/72h alerts.</p>
          </div>
          <div className="sla-actions">
            <button className="sla-button" onClick={handleExportCsv}>
              Export CSV
            </button>
            <button className="sla-button" onClick={fetchBacklog}>
              Refresh
            </button>
          </div>
        </header>

        <LoadingOverlay show={loading} ariaLabel="Loading SLA backlog" />
        {error && !loading && <div className="sla-state sla-state--error">{error}</div>}

        {!loading && !error && (
          <div className="sla-summary">
            <div className="sla-card">
              <span>Total backlog</span>
              <strong>{totals.total}</strong>
            </div>
            <div className="sla-card">
              <span>Processing</span>
              <strong>{totals.processing}</strong>
            </div>
            <div className="sla-card">
              <span>Picking</span>
              <strong>{totals.picking}</strong>
            </div>
            <div className="sla-card">
              <span>Completed 24h</span>
              <strong>{totals.completed24h}</strong>
            </div>
            <div className="sla-card sla-card--warn">
              <span>SLA48 overdue</span>
              <strong>{totals.sla48Overdue}</strong>
            </div>
            <div className="sla-card sla-card--danger">
              <span>SLA72 overdue</span>
              <strong>{totals.sla72Overdue}</strong>
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="sla-shipping">
            <h2>Backlog by Shipping</h2>
            <div className="sla-shipping-grid">
              {shippingBreakdown.map((row) => (
                <div key={row.label} className="sla-ship-card">
                  <div className="sla-ship-header">
                    <span>{row.label}</span>
                    <strong>{row.total}</strong>
                  </div>
                  <div className="sla-ship-flags">
                    <span className="sla-flag sla-flag--warn">SLA48 {row.sla48Overdue}</span>
                    <span className="sla-flag sla-flag--danger">SLA72 {row.sla72Overdue}</span>
                  </div>
                </div>
              ))}
              {shippingBreakdown.length === 0 && (
                <div className="sla-ship-empty">No backlog data.</div>
              )}
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="sla-filters">
            <label>
              Status
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="before-picking">Before picking</option>
                <option value="in-picking">In picking</option>
                <option value="completed-24h">Completed last 24h</option>
              </select>
            </label>
            <label>
              Shipping
              <select value={shippingFilter} onChange={(e) => setShippingFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="xpect">Same-day (Xpect)</option>
                <option value="next">Next-day</option>
                <option value="click">Click & Collect</option>
              </select>
            </label>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="sla-state sla-state--empty">
            <h3>No backlog found</h3>
            <p>Try adjusting the filters or refresh the data.</p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="sla-table">
            <div className="sla-row sla-row--head">
              <span>Order</span>
              <span>Status</span>
              <span>Shipping</span>
              <span>Created</span>
              <span>Picking at</span>
              <span>Complete at</span>
              <span>Age (h)</span>
              <span>Flags</span>
            </div>
            {filtered.map((order) => (
              <div key={order.increment_id || order.entity_id} className="sla-row">
                <span>#{order.increment_id}</span>
                <span>{order.statusLabel}</span>
                <span>{order.shippingLabel}</span>
                <span>{formatMagentoDateTime(order.created_at)}</span>
                <span>{order.sla?.pickingAt ? formatMagentoDateTime(order.sla.pickingAt) : '-'}</span>
                <span>{order.sla?.completeAt ? formatMagentoDateTime(order.sla.completeAt) : '-'}</span>
                <span>{order.ageHours !== null && order.ageHours !== undefined ? order.ageHours.toFixed(1) : '-'}</span>
                <span className="sla-flags">
                  {order.sla?.risk48 && !order.sla48Overdue && (
                    <span className="sla-flag sla-flag--warn">Risk SLA48</span>
                  )}
                  {order.sla48Overdue && <span className="sla-flag sla-flag--warn">SLA48 overdue</span>}
                  {order.sla?.risk72 && !order.sla72Overdue && (
                    <span className="sla-flag sla-flag--danger">Risk SLA72</span>
                  )}
                  {order.sla72Overdue && <span className="sla-flag sla-flag--danger">SLA72 overdue</span>}
                  {!order.sla48Overdue && !order.sla72Overdue && <span className="sla-flag">On track</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SlaMonitorPage;
